/**
 * GenericHSMSigner - Generic HSM Signer
 *
 * Một class duy nhất cho TẤT CẢ các HSM providers.
 * Cấu hình qua HSMEndpointConfig.
 */

import { AbstractHSMSigner } from '../AbstractHSMSigner';
import {
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../../types';

/**
 * Cấu hình endpoints cho HSM Provider
 */
export interface HSMEndpointConfig {
  /** Tên provider */
  provider: SignProvider;
  /** Tên hiển thị */
  displayName: string;
  /** Server URL */
  serverUrl: string;
  /** API Key */
  apiKey?: string;
  /** Endpoints */
  endpoints: {
    /** Xác thực */
    auth?: string;
    /** Lấy certificate */
    certificate: string;
    /** Ký file */
    signFile: string;
    /** Ký XML */
    signXml: string;
    /** Ký batch */
    signBatch?: string;
    /** Health check */
    health?: string;
  };
  /** Auth header name */
  authHeader?: string;
  /** Token prefix (Bearer, etc.) */
  tokenPrefix?: string;
}

/**
 * Generic HSM Signer - Cấu hình qua config
 */
export class GenericHSMSigner extends AbstractHSMSigner {
  readonly signProvider: SignProvider;
  readonly displayName: string;

  private endpointConfig: HSMEndpointConfig;

  constructor(config: HSMEndpointConfig) {
    super({
      serverUrl: config.serverUrl,
      apiKey: config.apiKey,
    });
    this.signProvider = config.provider;
    this.displayName = config.displayName;
    this.endpointConfig = config;
  }

  protected async authenticate(): Promise<string> {
    if (!this.endpointConfig.endpoints.auth) {
      return this.config.apiKey || '';
    }

    const response = await fetch(`${this.config.serverUrl}${this.endpointConfig.endpoints.auth}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.apiKey && { 'X-API-Key': this.config.apiKey }),
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.token || data.accessToken || data.access_token || '';
    }

    throw new Error(`${this.displayName} authentication failed`);
  }

  protected getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const headerName = this.endpointConfig.authHeader || 'Authorization';
    const prefix = this.endpointConfig.tokenPrefix || 'Bearer';

    if (this.authToken) {
      headers[headerName] = `${prefix} ${this.authToken}`;
    }

    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    return headers;
  }

  async getCertificateInfo(): Promise<CertificateInfo | null> {
    if (!this._isReady) await this.initialize();

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.certificate}`,
        { headers: this.getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        return {
          serialNumber: data.serialNumber || 'N/A',
          subject: data.subject || '',
          issuer: data.issuer || this.displayName,
          validFrom: new Date(data.validFrom || Date.now()),
          validTo: new Date(data.validTo || Date.now() + 365 * 24 * 60 * 60 * 1000),
          isValid: data.isValid ?? true,
        };
      }
    } catch {}
    return null;
  }

  async signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult> {
    return this.signFile(pdfData, 'document.pdf', options);
  }

  async signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this._isReady) {
      const ok = await this.initialize();
      if (!ok) return this.createErrorResult(`${this.displayName} chưa sẵn sàng`);
    }

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.signFile}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            fileData,
            fileName,
            reason: options?.reason,
            location: options?.location,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success !== false && (data.signedFile || data.signedData)) {
          return this.createSuccessResult(data.signedFile || data.signedData, {
            signerName: data.signerName || this.displayName,
            signedAt: new Date(),
            serialNumber: data.serialNumber,
          });
        }
        return this.createErrorResult(data.error || data.message || 'Ký file thất bại');
      }

      const errorData = await response.json().catch(() => ({}));
      return this.createErrorResult(errorData.message || errorData.error || 'HSM server error');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  async signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult> {
    if (!this._isReady) {
      const ok = await this.initialize();
      if (!ok) return this.createErrorResult(`${this.displayName} chưa sẵn sàng`);
    }

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.signXml}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            xmlContent,
            signatureType: options?.signatureType || 'enveloped',
            tagToSign: options?.tagToSign,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success !== false && (data.signedXml || data.signedData)) {
          return this.createSuccessResult(data.signedXml || data.signedData);
        }
        return this.createErrorResult(data.error || 'Ký XML thất bại');
      }

      return this.createErrorResult('HSM XML signing failed');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    if (!this._isReady) await this.initialize();

    const batchEndpoint = this.endpointConfig.endpoints.signBatch;
    if (!batchEndpoint) {
      // Fallback: sign each file individually
      return this.signBatchFallback(items, options);
    }

    try {
      const response = await fetch(`${this.config.serverUrl}${batchEndpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          files: items,
          reason: options?.reason,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: data.success,
          results: data.results || [],
        };
      }

      return {
        success: false,
        results: items.map((i) => ({ fileName: i.fileName, error: 'Batch failed' })),
      };
    } catch (error: any) {
      return {
        success: false,
        results: items.map((i) => ({ fileName: i.fileName, error: error.message })),
      };
    }
  }

  /**
   * Fallback: Sign each file individually when batch endpoint is not available
   */
  private async signBatchFallback(
    items: BatchSigningItem[],
    options?: SigningOptions
  ): Promise<BatchSigningResult> {
    const results: { fileName: string; signedData?: string; error?: string }[] = [];

    for (const item of items) {
      const result = await this.signFile(item.content, item.fileName, options);
      results.push({
        fileName: item.fileName,
        signedData: result.signedData,
        error: result.error,
      });
    }

    const allSuccess = results.every((r) => !r.error);
    return { success: allSuccess, results };
  }
}
